import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { entregaService } from "../services/entregaService";
import {
  filaEntregaService,
  UpdateFilaEntregaData,
} from "../services/filaEntregaService";
import { useGlobalStore } from "../store/globalStore";
import { getErrorMessage } from "../utils/errorHandler";
import { BrazilDate } from "../utils/BrazilDate";

export interface RankingDiaEntregaFilters {
  lojaId?: number | null;
}

export const useRankingDiaEntrega = (
  filters?: RankingDiaEntregaFilters,
  options?: { refetchInterval?: number | false }
) => {
  return useQuery({
    queryKey: ["rankingDiaEntrega", filters],
    queryFn: () => entregaService.getRankingDia(filters),
    refetchInterval: options?.refetchInterval,
  });
};

export const useCreateEntrega = () => {
  const queryClient = useQueryClient();
  const { setError } = useGlobalStore();

  return useMutation({
    mutationFn: (data: {
      lojaId: number;
      entregadorId: number;
      quantidadeEnderecos: number;
      pedidosDisplayIds?: number[];
      entregaExterna?: boolean;
      pedidosExternosDetalhes?: Array<{
        displayId: number;
        quantidadePizzas?: number;
      }>;
    }) =>
      entregaService.create({
        dataInicio: BrazilDate.now().toISOString(),
        lojaId: data.lojaId,
        entregadorId: data.entregadorId,
        quantidadeEnderecos: data.quantidadeEnderecos,
        ...(data.pedidosDisplayIds?.length && {
          pedidosDisplayIds: data.pedidosDisplayIds,
        }),
        ...(data.entregaExterna && { entregaExterna: true }),
        ...(data.pedidosExternosDetalhes?.length && {
          pedidosExternosDetalhes: data.pedidosExternosDetalhes,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rankingDiaEntrega"] });
      queryClient.invalidateQueries({ queryKey: ["entregas"] });
      queryClient.invalidateQueries({ queryKey: ["filaEntregas"] });
      queryClient.invalidateQueries({ queryKey: ["aguardando-entrega"] });
    },
    onError: (error) => setError(getErrorMessage(error)),
  });
};

export const useUpdateFilaEntregaStatus = () => {
  const queryClient = useQueryClient();
  const { setError } = useGlobalStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFilaEntregaData }) =>
      filaEntregaService.updateFilaEntrega(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filaEntregas"] });
      queryClient.invalidateQueries({ queryKey: ["rankingDiaEntrega"] });
    },
    onError: (error) => setError(getErrorMessage(error)),
  });
};
